import React from "react";
import { Phone, Mail } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import Photo from "../components/site/Photo";
import QualifyingForm from "../components/site/QualifyingForm";
import { Breadcrumb, SisterServices, FAQSection } from "../components/site/ServiceSections";

export default function GetAQuote() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <FormBlock />
      <WhatHappensNext />
      <SisterServices keys={["menu", "food", "venue", "brand"]} />
      <FAQSection
        eyebrow="Common questions · Quotes & timing"
        headline="Pricing, scope,"
        italic="and turnaround."
        intro="If your question isn't covered below, the fastest path is to call directly — most quotes are confirmed in under five minutes."
        faqs={FAQS}
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[55svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="menu-malai-tikka" alt="Get a Quote — Restaurant Photography Melbourne" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[55svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Get a Quote" />

      <div className="flex-1 flex flex-col justify-center max-w-4xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Request a written quote · Hand-confirmed within one business day
        </p>
        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}>
          The more you share,
          <br />
          the tighter <span className="italic font-light text-ember">we quote.</span>
        </h1>
        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Five short blocks. Most people finish it in under three minutes. We
          read every brief personally and respond by phone or email within one
          business day.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">
          <a href="tel:0494756111" className="inline-flex items-center gap-3 group">
            <Phone size={16} strokeWidth={1.6} className="text-ember" />
            <span className="font-sans font-bold text-base tracking-tight group-hover:text-ember transition-colors">
              Or call 0494 756 111
            </span>
          </a>
          <a href="mailto:hello@restaurantphotographer.com.au" className="inline-flex items-center gap-3 group">
            <Mail size={16} strokeWidth={1.6} className="text-ember" />
            <span className="font-sans font-bold text-sm tracking-tight group-hover:text-ember transition-colors break-all">
              hello@restaurantphotographer.com.au
            </span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const FormBlock = () => (
  <section id="enquiry" className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
      <div className="mb-14 lg:mb-16">
        <p className="text-xs uppercase tracking-wider2 text-ember mb-6 font-bold">Quote brief · Five quick blocks</p>
        <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
          Tell us about
          <br />
          <span className="italic font-light text-ember">your shoot.</span>
        </h2>
      </div>

      <div className="bg-bone border border-ink/10 p-7 md:p-10 lg:p-14">
        <QualifyingForm source="get-a-quote" />
      </div>
    </div>
  </section>
);

const WhatHappensNext = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-6 font-bold">
            What happens next · From submit to shoot
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
            Four steps,
            <br />
            <span className="italic font-light text-ember">zero guesswork.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            We respond by phone where possible — much faster than chasing email
            threads. Most venues book and shoot within 7–14 days of the brief.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {[
          ["01", "Brief received", "We read your submission within business hours. Often the same hour."],
          ["02", "Quote returned", "Written quote with deliverables, licence and date options — same business day."],
          ["03", "Shoot locked", "25% deposit secures the date. Balance on delivery. No surprise invoices."],
          ["04", "Files delivered", "Edited masters + channel crops in 5–7 business days. One shareable folder."],
        ].map(([n, t, d]) => (
          <div key={n} className="bg-ink p-7 md:p-8">
            <span className="font-display italic text-2xl text-ember leading-none">{n}</span>
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-3">{t}</h3>
            <p className="mt-4 text-sm text-bone/75 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: "Why so many fields on this form?", a: "Honestly — the more context you share, the tighter we can quote and the faster we can lock a shoot date. Every block is optional except your name, venue and email. Most people finish in under three minutes." },
  { q: "How fast do you respond?", a: "Standard response is within one business day, usually within a few hours during business hours. Urgent briefs flagged in the form get priority." },
  { q: "Will you call or email me back?", a: "Phone is much faster — we'll call back unless you specifically prefer email. Most quotes are confirmed in under five minutes on the call." },
  { q: "Can I get pricing without submitting this form?", a: "Yes — head to /packages for indicative pricing on the three main package shapes, or just call 0494 756 111 directly for a verbal quote in under five minutes." },
  { q: "Do you charge for quotes or consultations?", a: "Never. Quotes and pre-shoot consultations are free. We only invoice when you've confirmed a shoot date in writing." },
  { q: "What if my brief doesn't fit the package shapes?", a: "Bespoke briefs are normal — multi-venue groups, franchise rollouts, regional travel, custom retainers. Mention it in the message field or call directly and we'll scope a custom package." },
];
